// Two-column shell with grouped sidebar, sticky topbar, and a mobile
// drawer (Radix Sheet) holding the same nav. Mirrors
// apps/dashboard/src/components/layout/app-shell.tsx — see that file
// for the full geometry rationale (icon-column invariance,
// nested-tooltip timing, keyboard shortcut). Differences:
//
//   • No SubscriptionStatusBanner — admin operators don't have a
//     subscription state of their own.
//   • Brand mark reads "Cortex Admin" instead of "Cortex" so the two
//     apps cannot be confused at a glance.
//   • Topbar shows the global Cmd+K palette trigger as a hint.

import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { Menu, PanelLeftClose, PanelLeftOpen, Search, Sparkles } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../ui/sheet.js';
import { Separator } from '../ui/separator.js';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip.js';
import { ErrorBoundary } from '../error-boundary.js';
import { SidebarNav, RailSlot } from './sidebar-nav.js';
import { ThemeToggle } from './theme-toggle.js';
import { CommandPalette } from './command-palette.js';
import { cn } from '../../lib/utils.js';
import { useSidebarState } from '../../lib/sidebar-state.js';

function BrandMark({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center">
      <span className="flex h-14 w-[var(--sidebar-rail-w)] shrink-0 items-center justify-center">
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm"
        >
          <Sparkles className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]" />
        </span>
      </span>
      <span
        className={cn(
          'pr-4 text-sm font-semibold tracking-tight',
          collapsed ? 'sr-only' : 'whitespace-nowrap',
        )}
      >
        Cortex Admin
      </span>
    </div>
  );
}

function FooterRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-2 flex h-[var(--sidebar-row-h)] items-center">
      {children}
    </div>
  );
}

function CollapseToggleButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={collapsed}
          onClick={onToggle}
          className="ring-offset-card"
        >
          <Icon
            aria-hidden
            className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]"
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {label}{' '}
        <span className="ml-1 text-muted-foreground">
          (<kbd>⌘</kbd>/<kbd>Ctrl</kbd>+<kbd>B</kbd>)
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarFooter({ collapsed = false, onToggle }: { collapsed?: boolean; onToggle?: () => void }) {
  const { user } = useUser();
  const displayName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'Account';
  const subline = user?.primaryEmailAddress?.emailAddress;

  return (
    <div className="mt-auto border-t py-2">
      {onToggle && (
        <FooterRow>
          <RailSlot>
            <CollapseToggleButton collapsed={collapsed} onToggle={onToggle} />
          </RailSlot>
          <span
            className={cn(
              'pr-3 text-sm text-muted-foreground',
              collapsed ? 'sr-only' : 'whitespace-nowrap',
            )}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </span>
        </FooterRow>
      )}
      <FooterRow>
        <RailSlot>
          <ThemeToggle />
        </RailSlot>
        <span
          className={cn(
            'pr-3 text-sm text-muted-foreground',
            collapsed ? 'sr-only' : '',
          )}
        >
          Theme
        </span>
      </FooterRow>
      <Separator className="mx-2 my-2" />
      <FooterRow>
        <RailSlot>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <UserButton
                    appearance={{
                      elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' },
                    }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-medium">{displayName}</p>
                {subline && subline !== displayName && (
                  <p className="text-muted-foreground">{subline}</p>
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            <UserButton
              appearance={{
                elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' },
              }}
            />
          )}
        </RailSlot>
        <div
          className={cn(
            'min-w-0 flex-1 pr-3',
            collapsed ? 'sr-only' : '',
          )}
        >
          <p className="truncate text-sm font-medium">{displayName}</p>
          {subline && subline !== displayName && (
            <p className="truncate text-xs text-muted-foreground">{subline}</p>
          )}
        </div>
      </FooterRow>
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { isCollapsed, toggle } = useSidebarState();

  // Cmd/Ctrl+K toggles the command palette globally. Mirrors the Linear /
  // Stripe / Vercel admin UX. Listening at window-level so any child
  // component (table cells, dialogs, etc.) doesn't accidentally swallow
  // the binding.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="flex h-full">
      <TooltipProvider delayDuration={120} skipDelayDuration={0}>
        <aside
          data-collapsed={isCollapsed}
          className={cn(
            'hidden shrink-0 flex-col overflow-hidden border-r bg-card transition-[width] duration-200 ease-out md:flex',
          )}
          style={{
            width: isCollapsed ? 'var(--sidebar-rail-w)' : 'var(--sidebar-expanded-w)',
          }}
          aria-label="Primary navigation"
        >
          <div className="flex h-14 items-center border-b">
            <BrandMark collapsed={isCollapsed} />
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            <SidebarNav collapsed={isCollapsed} />
          </div>
          <SidebarFooter collapsed={isCollapsed} onToggle={toggle} />
        </aside>
      </TooltipProvider>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-card/85 px-4 backdrop-blur-sm md:px-6">
          <div className="flex items-center gap-3 md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Open menu">
                  <Menu aria-hidden />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-72 flex-col p-0">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <TooltipProvider delayDuration={120} skipDelayDuration={0}>
                  <div className="flex h-14 items-center border-b">
                    <BrandMark />
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <SidebarNav
                      onNavigate={() => {
                        setMobileOpen(false);
                      }}
                    />
                  </div>
                  <SidebarFooter />
                </TooltipProvider>
              </SheetContent>
            </Sheet>
            <BrandMark />
          </div>

          {/* Cmd+K trigger — desktop. Reads as a search field so the
              affordance is discoverable; clicking opens the same palette
              the keyboard shortcut does. */}
          <button
            type="button"
            onClick={() => {
              setPaletteOpen(true);
            }}
            className="hidden h-9 w-72 items-center gap-2 rounded-md border bg-background/80 px-3 text-sm text-muted-foreground transition-colors hover:bg-background md:inline-flex"
            aria-label="Open command palette"
          >
            <Search aria-hidden className="h-4 w-4" />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="rounded border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px]">
              ⌘K
            </kbd>
          </button>
          <div className="hidden flex-1 md:block" />
          <div className="flex items-center gap-2 md:hidden">
            <UserButton
              appearance={{ elements: { rootBox: 'h-8 w-8', userButtonAvatarBox: 'h-8 w-8' } }}
            />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full min-w-0 max-w-screen-2xl p-4 sm:p-6 lg:p-8">
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNavigate={(to) => {
          setPaletteOpen(false);
          navigate(to);
        }}
      />
    </div>
  );
}
